import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { getMicrophoneErrorKey, MicrophoneError, requestMicrophoneStream } from '../../../shared/utils/microphone';

import {
  ConversationVoiceCapabilities,
  ConversationVoiceSession,
  ConversationVoiceSpeechDelivery,
  ConversationVoiceTurnResult,
  endConversationVoiceSession,
  interruptConversationVoiceSession,
  getConversationVoiceCapabilities,
  startConversationVoiceSession,
  streamConversationVoiceSpeech,
  submitConversationVoiceTurn,
} from '../services/conversationVoiceService';
import { PcmStreamPlayer } from '../utils/pcmStreamPlayer';
import { defaultVoiceEndpointing, readAnalyserRms } from '../utils/voiceVad';
import { buildConversationVoiceRealtimeUrl, startPcmCapture } from '../utils/voiceRealtimeSocket';
import { finalizeLastVoiceTurn, upsertVoiceTurn, VoiceCallTurn } from '../utils/voiceTurns';

export type AiVoiceSessionStatus =
  | 'idle'
  | 'connecting'
  | 'ready'
  | 'recording'
  | 'processing'
  | 'speaking'
  | 'error';

const preferredMimeTypes = ['audio/webm;codecs=opus', 'audio/webm', 'audio/ogg;codecs=opus'];

const decodeBase64Audio = (base64: string, contentType = 'audio/mpeg'): Blob => {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let index = 0; index < binary.length; index += 1) {
    bytes[index] = binary.charCodeAt(index);
  }
  return new Blob([bytes], { type: contentType });
};

const isAbortError = (error: unknown): boolean =>
  Boolean(
    error
    && typeof error === 'object'
    && 'name' in error
    && (error as { name?: string }).name === 'AbortError',
  );

export const useAiVoiceSession = (conversationId: string | null, enabled: boolean) => {
  const { t } = useTranslation('common');
  const [capabilities, setCapabilities] = useState<ConversationVoiceCapabilities | null>(null);
  const [capabilitiesLoading, setCapabilitiesLoading] = useState(false);
  const [status, setStatus] = useState<AiVoiceSessionStatus>('idle');
  const [error, setError] = useState<string | null>(null);
  const [transcript, setTranscript] = useState<string | null>(null);
  const [assistantText, setAssistantText] = useState<string | null>(null);
  const [turns, setTurns] = useState<VoiceCallTurn[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [micPaused, setMicPaused] = useState(false);
  const [level, setLevel] = useState(0);

  const sessionRef = useRef<ConversationVoiceSession | null>(null);
  const capabilitiesRef = useRef<ConversationVoiceCapabilities | null>(null);
  const recorderRef = useRef<MediaRecorder | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const audioUrlRef = useRef<string | null>(null);
  const pcmPlayerRef = useRef<PcmStreamPlayer | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const sourceNodeRef = useRef<MediaStreamAudioSourceNode | null>(null);
  const vadTimerRef = useRef<number | null>(null);
  const speechCandidateAtRef = useRef(0);
  const silenceStartedAtRef = useRef(0);
  const utteranceStartedAtRef = useRef(0);
  const speakingStartedAtRef = useRef(0);
  const turnAbortRef = useRef<AbortController | null>(null);
  const speechAbortRef = useRef<AbortController | null>(null);
  const turnGenerationRef = useRef(0);
  const statusRef = useRef<AiVoiceSessionStatus>('idle');
  const micPausedRef = useRef(false);
  const duplexRef = useRef(false);
  const keepStreamRef = useRef(false);
  const realtimeActiveRef = useRef(false);
  const realtimeSocketRef = useRef<WebSocket | null>(null);
  const pcmCaptureStopRef = useRef<(() => void) | null>(null);
  const dropRealtimeAudioRef = useRef(false);
  const conversationIdRef = useRef(conversationId);
  conversationIdRef.current = conversationId;
  capabilitiesRef.current = capabilities;
  statusRef.current = status;
  micPausedRef.current = micPaused;

  const stopPlayback = useCallback(() => {
    speechAbortRef.current?.abort();
    speechAbortRef.current = null;
    pcmPlayerRef.current?.stop();
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.src = '';
      audioRef.current = null;
    }
    if (audioUrlRef.current) {
      URL.revokeObjectURL(audioUrlRef.current);
      audioUrlRef.current = null;
    }
  }, []);

  const stopVad = useCallback(() => {
    if (vadTimerRef.current !== null) {
      window.clearInterval(vadTimerRef.current);
      vadTimerRef.current = null;
    }
    speechCandidateAtRef.current = 0;
    silenceStartedAtRef.current = 0;
    setLevel(0);
  }, []);

  const stopRecorderKeepStream = useCallback(() => {
    recorderRef.current = null;
    chunksRef.current = [];
  }, []);

  const stopMediaTracks = useCallback(() => {
    sourceNodeRef.current?.disconnect();
    sourceNodeRef.current = null;
    analyserRef.current = null;
    streamRef.current?.getTracks().forEach(track => track.stop());
    streamRef.current = null;
    recorderRef.current = null;
    chunksRef.current = [];
  }, []);

  const closeAudioGraph = useCallback(async () => {
    stopVad();
    const context = audioContextRef.current;
    audioContextRef.current = null;
    analyserRef.current = null;
    sourceNodeRef.current = null;
    await pcmPlayerRef.current?.close();
    pcmPlayerRef.current = null;
    if (context && context.state !== 'closed') {
      await context.close().catch(() => undefined);
    }
  }, [stopVad]);

  const closeSession = useCallback(async (options?: { silent?: boolean }) => {
    const session = sessionRef.current;
    const activeConversationId = conversationIdRef.current;
    turnGenerationRef.current += 1;
    turnAbortRef.current?.abort();
    turnAbortRef.current = null;
    sessionRef.current = null;
    duplexRef.current = false;
    keepStreamRef.current = false;
    realtimeActiveRef.current = false;
    dropRealtimeAudioRef.current = false;
    pcmCaptureStopRef.current?.();
    pcmCaptureStopRef.current = null;
    const socket = realtimeSocketRef.current;
    realtimeSocketRef.current = null;
    if (socket && (socket.readyState === WebSocket.OPEN || socket.readyState === WebSocket.CONNECTING)) {
      try {
        if (socket.readyState === WebSocket.OPEN) {
          socket.send(JSON.stringify({ type: 'close' }));
        }
        socket.close();
      } catch {
        // Hangup is best-effort.
      }
    }
    stopPlayback();
    if (recorderRef.current && recorderRef.current.state !== 'inactive') {
      recorderRef.current.onstop = null;
      recorderRef.current.stop();
    }
    stopMediaTracks();
    await closeAudioGraph();
    setIsOpen(false);
    setStatus('idle');
    setTranscript(null);
    setAssistantText(null);
    setTurns([]);
    setMicPaused(false);
    if (!options?.silent) {
      setError(null);
    }

    if (session && activeConversationId) {
      try {
        await endConversationVoiceSession(activeConversationId, session.sessionId);
      } catch {
        // Hangup is best-effort; the session TTL will expire it.
      }
    }
  }, [closeAudioGraph, stopMediaTracks, stopPlayback]);

  const activeConversationRef = useRef(conversationId);
  useEffect(() => {
    if (activeConversationRef.current === conversationId) {
      return;
    }

    activeConversationRef.current = conversationId;
    void closeSession({ silent: true });
  }, [closeSession, conversationId]);

  useEffect(() => {
    if (!enabled || !conversationId) {
      setCapabilities(null);
      setCapabilitiesLoading(false);
      void closeSession({ silent: true });
      return;
    }

    let cancelled = false;
    setCapabilitiesLoading(true);
    void getConversationVoiceCapabilities(conversationId)
      .then(result => {
        if (!cancelled) {
          setCapabilities(result);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setCapabilities(null);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setCapabilitiesLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [closeSession, conversationId, enabled]);

  useEffect(() => () => {
    void closeSession({ silent: true });
  }, [closeSession]);

  const playInlineAudio = useCallback(async (turn: ConversationVoiceTurnResult) => {
    if (!turn.audioBase64) {
      setStatus('ready');
      return;
    }

    stopPlayback();
    const blob = decodeBase64Audio(turn.audioBase64, turn.audioContentType);
    const objectUrl = URL.createObjectURL(blob);
    audioUrlRef.current = objectUrl;
    const audio = new Audio(objectUrl);
    audioRef.current = audio;
    speakingStartedAtRef.current = Date.now();
    setStatus('speaking');

    await new Promise<void>(resolve => {
      audio.onended = () => resolve();
      audio.onerror = () => resolve();
      void audio.play().catch(() => resolve());
    });

    if (audioRef.current === audio) {
      stopPlayback();
      setStatus(current => (current === 'speaking' ? 'ready' : current));
    }
  }, [stopPlayback]);

  const playStreamedSpeech = useCallback(async (
    conversationIdValue: string,
    session: ConversationVoiceSession,
    turn: ConversationVoiceTurnResult,
    generation: number,
  ) => {
    stopPlayback();
    const abort = new AbortController();
    speechAbortRef.current = abort;
    speakingStartedAtRef.current = Date.now();
    setStatus('speaking');

    const streamed = await streamConversationVoiceSpeech(conversationIdValue, session.sessionId, {
      text: turn.assistantMessage?.content,
      assistantMessageId: turn.assistantMessage?.id,
      signal: abort.signal,
    });

    if (turnGenerationRef.current !== generation || abort.signal.aborted) {
      return;
    }

    const isPcm = streamed.format === 'pcm' || streamed.contentType.includes('pcm');
    if (!isPcm) {
      const buffer = await streamed.response.arrayBuffer();
      if (turnGenerationRef.current !== generation || abort.signal.aborted) {
        return;
      }
      const objectUrl = URL.createObjectURL(new Blob([buffer], { type: streamed.contentType || 'audio/mpeg' }));
      audioUrlRef.current = objectUrl;
      const audio = new Audio(objectUrl);
      audioRef.current = audio;
      await new Promise<void>(resolve => {
        audio.onended = () => resolve();
        audio.onerror = () => resolve();
        void audio.play().catch(() => resolve());
      });
      if (audioRef.current === audio) {
        stopPlayback();
        setStatus(current => (current === 'speaking' ? 'ready' : current));
      }
      return;
    }

    const player = new PcmStreamPlayer();
    player.setSampleRate(streamed.sampleRate);
    pcmPlayerRef.current = player;
    const reader = streamed.response.body?.getReader();
    if (!reader) {
      const buffer = await streamed.response.arrayBuffer();
      await player.push(new Uint8Array(buffer));
      player.markStreamComplete();
      await player.waitUntilEnded();
      if (turnGenerationRef.current === generation) {
        setStatus(current => (current === 'speaking' ? 'ready' : current));
      }
      return;
    }

    while (true) {
      const { done, value } = await reader.read();
      if (done) {
        break;
      }
      if (value) {
        await player.push(value);
      }
      if (turnGenerationRef.current !== generation || abort.signal.aborted) {
        player.stop();
        return;
      }
    }

    player.markStreamComplete();
    await player.waitUntilEnded();
    if (turnGenerationRef.current === generation && statusRef.current === 'speaking') {
      setStatus('ready');
    }
  }, [stopPlayback]);

  const playTurnSpeech = useCallback(async (
    turn: ConversationVoiceTurnResult,
    generation: number,
  ) => {
    const session = sessionRef.current;
    const activeConversationId = conversationIdRef.current;
    const delivery: ConversationVoiceSpeechDelivery = turn.speechDelivery
      || session?.speechDelivery
      || capabilitiesRef.current?.speechDelivery
      || 'inline';

    if (turn.skipped || !turn.assistantMessage?.content) {
      setStatus('ready');
      return;
    }

    if (delivery === 'none') {
      setStatus('ready');
      return;
    }

    if (delivery === 'stream' && session && activeConversationId) {
      try {
        await playStreamedSpeech(activeConversationId, session, turn, generation);
      } catch (playError) {
        if (isAbortError(playError) || turnGenerationRef.current !== generation) {
          return;
        }
        setStatus('ready');
      }
      return;
    }

    await playInlineAudio(turn);
  }, [playInlineAudio, playStreamedSpeech]);

  const submitRecording = useCallback(async (blob: Blob, mimeType: string) => {
    const session = sessionRef.current;
    const activeConversationId = conversationIdRef.current;
    if (!session || !activeConversationId || blob.size <= 0) {
      setStatus('ready');
      return;
    }

    const generation = turnGenerationRef.current + 1;
    turnGenerationRef.current = generation;
    turnAbortRef.current?.abort();
    const abort = new AbortController();
    turnAbortRef.current = abort;
    setStatus('processing');
    try {
      const extension = mimeType.includes('ogg') ? 'ogg' : 'webm';
      const result = await submitConversationVoiceTurn(
        activeConversationId,
        session.sessionId,
        blob,
        `voice-turn.${extension}`,
        {
          speechDelivery: session.speechDelivery || capabilitiesRef.current?.speechDelivery,
          signal: abort.signal,
        },
      );
      if (turnGenerationRef.current !== generation || sessionRef.current?.sessionId !== session.sessionId) {
        return;
      }
      if (result.skipped) {
        setStatus('ready');
        return;
      }
      setTranscript(result.transcript);
      setAssistantText(result.assistantMessage?.content || null);
      setTurns(current => {
        let next = current;
        if (result.transcript.trim()) {
          next = upsertVoiceTurn(next, 'user', result.transcript, true);
        }
        const assistant = result.assistantMessage?.content?.trim();
        if (assistant) {
          next = upsertVoiceTurn(next, 'assistant', assistant, true);
        }
        return next;
      });
      await playTurnSpeech(result, generation);
    } catch (submitError) {
      if (isAbortError(submitError) || turnGenerationRef.current !== generation) {
        return;
      }
      setStatus('error');
      setError(submitError instanceof Error ? submitError.message : 'Voice call failed');
    }
  }, [playTurnSpeech]);

  /**
   * A failed microphone must not reach the user as the browser's own wording, which says things like
   * "Requested device not found" instead of telling them to plug one in.
   */
  const describeError = useCallback((error: unknown, fallback: string) => {
    if (error instanceof MicrophoneError) {
      return t(getMicrophoneErrorKey(error));
    }

    return error instanceof Error ? error.message : fallback;
  }, [t]);

  const acquireMic = useCallback(async () => {
    if (streamRef.current) {
      return streamRef.current;
    }

    const stream = await requestMicrophoneStream({
      echoCancellation: true,
      noiseSuppression: true,
      autoGainControl: true,
      channelCount: 1,
    });
    streamRef.current = stream;
    return stream;
  }, []);

  const sendRealtimeControl = useCallback((payload: Record<string, unknown>) => {
    const socket = realtimeSocketRef.current;
    if (socket && socket.readyState === WebSocket.OPEN) {
      socket.send(JSON.stringify(payload));
    }
  }, []);

  const stopPcmCapture = useCallback(() => {
    pcmCaptureStopRef.current?.();
    pcmCaptureStopRef.current = null;
  }, []);

  const startRealtimeCapture = useCallback(async (sampleRate: number) => {
    stopPcmCapture();
    const stream = await acquireMic();
    const capture = startPcmCapture(stream, sampleRate, pcm => {
      const socket = realtimeSocketRef.current;
      if (socket && socket.readyState === WebSocket.OPEN && !micPausedRef.current) {
        socket.send(pcm);
      }
    });
    pcmCaptureStopRef.current = capture.stop;
  }, [acquireMic, stopPcmCapture]);

  const handleRealtimeMessage = useCallback((raw: string) => {
    let payload: any;
    try {
      payload = JSON.parse(raw);
    } catch {
      return;
    }

    const type = String(payload?.type ?? '');
    if (type === 'user_transcript' && typeof payload.text === 'string') {
      setTranscript(payload.text);
      setTurns(current => upsertVoiceTurn(current, 'user', payload.text, Boolean(payload.final)));
      if (payload.final) {
        setStatus('processing');
      }
      return;
    }
    if (type === 'assistant_transcript' && typeof payload.text === 'string') {
      dropRealtimeAudioRef.current = false;
      setAssistantText(payload.text);
      setTurns(current => upsertVoiceTurn(current, 'assistant', payload.text, Boolean(payload.final)));
      if (!payload.final) {
        setStatus('speaking');
      }
      return;
    }
    if (type === 'speech_started') {
      setTurns(current => finalizeLastVoiceTurn(current, 'assistant'));
      setStatus('recording');
      return;
    }
    if (type === 'speech_stopped') {
      dropRealtimeAudioRef.current = false;
      setStatus(current => (current === 'recording' ? 'processing' : current));
      return;
    }
    if (type === 'interrupted') {
      const wasSpeaking = statusRef.current === 'speaking';
      stopPlayback();
      if (wasSpeaking) {
        dropRealtimeAudioRef.current = true;
      }
      setTurns(current => finalizeLastVoiceTurn(current, 'assistant'));
      setStatus(current => (current === 'speaking' ? 'recording' : current));
      return;
    }
    if (type === 'response_started') {
      dropRealtimeAudioRef.current = false;
      return;
    }
    if (type === 'response_done') {
      pcmPlayerRef.current?.markStreamComplete();
      setTurns(current => finalizeLastVoiceTurn(current, 'assistant'));
      setStatus(current => (current === 'speaking' || current === 'processing' ? 'ready' : current));
      return;
    }
    if (type === 'error') {
      setStatus('error');
      setError(
        payload?.code === 'VOICE_TOKEN_QUOTA_EXCEEDED'
          ? 'This workspace has used its live voice token allowance.'
          : payload?.code === 'AI_TOKEN_QUOTA_EXCEEDED'
            ? 'This workspace has used its AI token allowance.'
            : (typeof payload.message === 'string' ? payload.message : 'Live voice failed'),
      );
    }
  }, [stopPlayback]);

  const connectRealtime = useCallback(async (session: ConversationVoiceSession) => {
    if (!session.realtimePath) {
      throw new Error('Realtime voice path is missing.');
    }

    const socket = new WebSocket(buildConversationVoiceRealtimeUrl(session.realtimePath));
    socket.binaryType = 'arraybuffer';
    realtimeSocketRef.current = socket;

    await new Promise<void>((resolve, reject) => {
      const timeout = window.setTimeout(() => {
        reject(new Error('Live voice connection timed out.'));
      }, 8000);
      socket.onopen = () => undefined;
      socket.onerror = () => {
        window.clearTimeout(timeout);
        reject(new Error('Live voice connection failed.'));
      };
      socket.onmessage = event => {
        if (typeof event.data !== 'string') {
          if (dropRealtimeAudioRef.current) {
            return;
          }
          const bytes = event.data instanceof ArrayBuffer
            ? new Uint8Array(event.data)
            : new Uint8Array(event.data as ArrayBuffer);
          if (!pcmPlayerRef.current) {
            const player = new PcmStreamPlayer();
            player.setSampleRate(capabilitiesRef.current?.streamSampleRate || 24000);
            pcmPlayerRef.current = player;
          }
          setStatus('speaking');
          void pcmPlayerRef.current.push(bytes);
          return;
        }
        let payload: any = null;
        try {
          payload = JSON.parse(event.data);
        } catch {
          return;
        }
        if (payload?.type === 'ready') {
          window.clearTimeout(timeout);
          const sampleRate = Number(payload.sampleRate);
          if (Number.isFinite(sampleRate) && sampleRate > 0) {
            pcmPlayerRef.current?.setSampleRate(sampleRate);
          }
          resolve();
          return;
        }
        handleRealtimeMessage(event.data);
      };
      socket.onclose = () => {
        window.clearTimeout(timeout);
        if (realtimeSocketRef.current === socket) {
          realtimeSocketRef.current = null;
        }
      };
    });

    realtimeActiveRef.current = true;
    const sampleRate = capabilitiesRef.current?.streamSampleRate || 24000;
    if (duplexRef.current) {
      await startRealtimeCapture(sampleRate);
    }
  }, [handleRealtimeMessage, startRealtimeCapture]);

  const startTalking = useCallback(async () => {
    if (statusRef.current !== 'ready' && statusRef.current !== 'speaking' && statusRef.current !== 'processing') {
      return;
    }

    stopPlayback();
    if (realtimeActiveRef.current) {
      if (duplexRef.current) {
        return;
      }
      try {
        await startRealtimeCapture(capabilitiesRef.current?.streamSampleRate || 24000);
        setStatus('recording');
        setError(null);
      } catch (startError) {
        setError(describeError(startError, t('microphoneUnavailable')));
        setStatus('error');
      }
      return;
    }
    const existing = recorderRef.current;
    if (existing && existing.state !== 'inactive') {
      existing.onstop = null;
      existing.stop();
    }
    try {
      const stream = await acquireMic();
      const mimeType = preferredMimeTypes.find(type => MediaRecorder.isTypeSupported(type)) || '';
      const recorder = mimeType ? new MediaRecorder(stream, { mimeType }) : new MediaRecorder(stream);
      chunksRef.current = [];
      recorder.ondataavailable = event => {
        if (event.data.size > 0) {
          chunksRef.current.push(event.data);
        }
      };
      recorder.onerror = () => {
        setError('Audio recording failed. Please try again.');
        setStatus('error');
        if (!keepStreamRef.current) {
          stopMediaTracks();
        } else {
          stopRecorderKeepStream();
        }
      };
      recorderRef.current = recorder;
      utteranceStartedAtRef.current = Date.now();
      silenceStartedAtRef.current = 0;
      recorder.start();
      setStatus('recording');
      setError(null);
    } catch (startError) {
      setError(describeError(startError, t('microphoneUnavailable')));
      setStatus('error');
      if (!keepStreamRef.current) {
        stopMediaTracks();
      }
    }
  }, [acquireMic, describeError, startRealtimeCapture, stopMediaTracks, stopPlayback, stopRecorderKeepStream, t]);

  const stopTalking = useCallback(() => {
    if (realtimeActiveRef.current) {
      if (!duplexRef.current) {
        stopPcmCapture();
        sendRealtimeControl({ type: 'commit' });
        setStatus('processing');
      }
      return;
    }
    const recorder = recorderRef.current;
    if (!recorder || recorder.state === 'inactive') {
      return;
    }

    recorder.onstop = () => {
      const recordingMimeType = (recorder.mimeType || 'audio/webm').split(';')[0].trim() || 'audio/webm';
      const blob = new Blob(chunksRef.current, { type: recordingMimeType });
      if (!keepStreamRef.current) {
        stopMediaTracks();
      } else {
        stopRecorderKeepStream();
      }
      void submitRecording(blob, recordingMimeType);
    };
    recorder.stop();
  }, [sendRealtimeControl, stopMediaTracks, stopPcmCapture, stopRecorderKeepStream, submitRecording]);

  const interruptInFlight = useCallback(() => {
    turnGenerationRef.current += 1;
    turnAbortRef.current?.abort();
    turnAbortRef.current = null;
    stopPlayback();
    if (realtimeActiveRef.current) {
      sendRealtimeControl({ type: 'interrupt' });
      return;
    }
    const session = sessionRef.current;
    const activeConversationId = conversationIdRef.current;
    if (session && activeConversationId) {
      void interruptConversationVoiceSession(activeConversationId, session.sessionId).catch(() => undefined);
    }
  }, [sendRealtimeControl, stopPlayback]);

  const tickVad = useCallback(() => {
    const analyser = analyserRef.current;
    if (!analyser || !duplexRef.current || micPausedRef.current) {
      setLevel(0);
      return;
    }

    const capabilitiesValue = capabilitiesRef.current;
    const endpointing = defaultVoiceEndpointing(capabilitiesValue?.endpointing);
    const rms = readAnalyserRms(analyser);
    const normalizedLevel = Math.min(1, rms / Math.max(endpointing.vadRmsThreshold * 4, 0.02));
    setLevel(current => (Math.abs(current - normalizedLevel) > 0.08 ? normalizedLevel : current));
    if (realtimeActiveRef.current) {
      return;
    }

    const now = Date.now();
    const currentStatus = statusRef.current;
    const bargeIn = capabilitiesValue?.bargeInEnabled !== false;

    if (currentStatus === 'recording') {
      const elapsed = now - utteranceStartedAtRef.current;
      if (rms >= endpointing.vadRmsThreshold) {
        silenceStartedAtRef.current = 0;
      } else if (!silenceStartedAtRef.current) {
        silenceStartedAtRef.current = now;
      }

      const silentFor = silenceStartedAtRef.current ? now - silenceStartedAtRef.current : 0;
      if (
        elapsed >= endpointing.minUtteranceMs
        && (silentFor >= endpointing.silenceMs || elapsed >= endpointing.maxUtteranceSeconds * 1000)
      ) {
        stopTalking();
      }
      return;
    }

    const canBargeIn = bargeIn && (currentStatus === 'speaking' || currentStatus === 'processing');
    const threshold = canBargeIn ? endpointing.bargeInRmsThreshold : endpointing.vadRmsThreshold;
    const holdoffOk = !canBargeIn || now - speakingStartedAtRef.current >= endpointing.bargeInHoldoffMs;
    if ((currentStatus === 'ready' || canBargeIn) && holdoffOk) {
      if (rms >= threshold) {
        if (!speechCandidateAtRef.current) {
          speechCandidateAtRef.current = now;
        }
        if (now - speechCandidateAtRef.current >= endpointing.minUtteranceMs) {
          speechCandidateAtRef.current = 0;
          if (canBargeIn) {
            interruptInFlight();
          }
          void startTalking();
        }
      } else {
        speechCandidateAtRef.current = 0;
      }
    }
  }, [interruptInFlight, startTalking, stopTalking]);

  const startVad = useCallback(async (stream: MediaStream) => {
    const AudioContextCtor = window.AudioContext
      || (window as Window & { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!AudioContextCtor) {
      return;
    }

    const context = audioContextRef.current ?? new AudioContextCtor();
    audioContextRef.current = context;
    await context.resume().catch(() => undefined);
    sourceNodeRef.current?.disconnect();
    const source = context.createMediaStreamSource(stream);
    const analyser = context.createAnalyser();
    analyser.fftSize = 2048;
    analyser.smoothingTimeConstant = 0.35;
    source.connect(analyser);
    sourceNodeRef.current = source;
    analyserRef.current = analyser;
    if (vadTimerRef.current !== null) {
      window.clearInterval(vadTimerRef.current);
    }
    vadTimerRef.current = window.setInterval(tickVad, 50);
  }, [tickVad]);

  const startCall = useCallback(async () => {
    if (!conversationId) {
      return;
    }

    setError(null);
    setTranscript(null);
    setAssistantText(null);
    setTurns([]);
    setMicPaused(false);
    setIsOpen(true);
    setStatus('connecting');
    try {
      const session = await startConversationVoiceSession(conversationId);
      sessionRef.current = session;
      const mode = session.mode || capabilitiesRef.current?.mode || 'Duplex';
      duplexRef.current = mode === 'Duplex';
      keepStreamRef.current = duplexRef.current;
      const useRealtime = session.transport === 'realtime' && Boolean(session.realtimePath);
      if (duplexRef.current) {
        const stream = await acquireMic();
        await startVad(stream);
      }
      if (useRealtime) {
        try {
          await connectRealtime(session);
        } catch (realtimeError) {
          realtimeActiveRef.current = false;
          pcmCaptureStopRef.current?.();
          pcmCaptureStopRef.current = null;
          const socket = realtimeSocketRef.current;
          realtimeSocketRef.current = null;
          socket?.close();
          if (!duplexRef.current) {
            throw realtimeError;
          }
        }
      }
      speakingStartedAtRef.current = Date.now();
      setStatus('ready');
    } catch (startError) {
      setStatus('error');
      setError(describeError(startError, 'Voice call failed'));
    }
  }, [acquireMic, connectRealtime, conversationId, describeError, startVad]);

  const hangUp = useCallback(() => {
    void closeSession();
  }, [closeSession]);

  const toggleMicPaused = useCallback(() => {
    if (!duplexRef.current) {
      return;
    }
    setMicPaused(current => {
      const next = !current;
      if (realtimeActiveRef.current) {
        sendRealtimeControl({ type: 'mute', muted: next });
      }
      if (next) {
        speechCandidateAtRef.current = 0;
        if (statusRef.current === 'recording') {
          stopTalking();
        }
      }
      return next;
    });
  }, [sendRealtimeControl, stopTalking]);

  return {
    capabilities,
    capabilitiesLoading,
    isAvailable: Boolean(capabilities?.enabled && capabilities.available),
    isQuotaExceeded: Boolean(capabilities?.voiceTokenQuotaExceeded || capabilities?.aiTokenQuotaExceeded),
    isDuplex: (sessionRef.current?.mode || capabilities?.mode) === 'Duplex',
    isOpen,
    status,
    error,
    transcript,
    assistantText,
    turns,
    micPaused,
    level,
    startCall,
    startTalking,
    stopTalking,
    hangUp,
    toggleMicPaused,
  };
};
